import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Stack,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  Modal,
  Fade,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "../../App";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import logo from "../../assets/logo.svg";
import { downloadOrderPDF, getOrderPDF } from "../../service/orderService";

const OrderConfirmModal = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    id,
    order_number,
    order_date,
    delivery_date,
    total_amount,
    name,
  } = state || {};

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!state) {
      showSnackbar("Order details missing. Redirecting...", "error");
      navigate("/create-order");
    } else {
      setOpen(true);

      // New: Order creation toast
      const showToast = localStorage.getItem("showOrderSuccessToast");
      if (showToast === "1") {
        showSnackbar("Order submitted successfully", "success");
        localStorage.removeItem("showOrderSuccessToast");
      }

      // New: Order update toast
      const showUpdateToast = localStorage.getItem("showOrderUpdateToast");
      if (showUpdateToast === "1") {
        showSnackbar("Order updated successfully", "success");
        localStorage.removeItem("showOrderUpdateToast");
      }
    }
  }, [state, navigate, showSnackbar]);

  const handleClose = () => {
    setOpen(false);
    navigate("/orders");
  };

  const handleViewPDF = async () => {
    try {
      const response = await getOrderPDF(id);
      const pdfBlob = response.data;
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, "_blank");
    } catch (err) {
      showSnackbar("PDF file is not available.", "error");
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await downloadOrderPDF(id);
      const blob = response.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `order_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);
      showSnackbar("PDF downloaded successfully!", "success");
    } catch (err) {
      showSnackbar("Failed to download PDF.", "error");
    }
  };

  const handleShare = async () => {
    try {
      const response = await getOrderPDF(id);
      const file = new File([response.data], `order_${id}.pdf`, {
        type: "application/pdf",
      });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Order ${order_number}`,
          text: `Here is the PDF for order ${order_number}.`,
          files: [file],
        });
        showSnackbar("PDF shared successfully!", "success");
      } else {
        showSnackbar("PDF sharing not supported on this device.", "warning");
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to share the PDF.", "error");
    }
  };

  const handleTakeMoreOrder = () => {
    setOpen(false);
    navigate("/orders");
  };

  const renderActionButton = (icon, label, onClick, color, hoverColor) => (
    <Button
      variant="contained"
      startIcon={icon}
      onClick={onClick}
      size={isMobile ? "small" : "large"}
      sx={{
        background: color,
        color: "#FFFFFF",
        borderRadius: 2,
        py: { xs: 0.5, sm: 1.5 },
        px: { xs: 0.5, sm: 2 },
        width: { xs: 40, sm: "auto" },
        height: { xs: 40, sm: "auto" },
        fontFamily: "'Roboto', sans-serif",
        fontSize: { xs: "0.8rem", sm: "1rem" },
        fontWeight: 500,
        textTransform: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: { xs: "center", sm: "flex-start" },
        "&:hover": {
          background: hoverColor,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
          transform: "scale(1.02)",
        },
        transition: "all 0.2s ease",
      }}
    >
      <Typography
        sx={{
          display: { xs: "none", sm: "block" },
          ml: 1,
        }}
      >
        {label}
      </Typography>
    </Button>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(2px)", // reduced blur
      }}
    >
      <Fade in={open}>
        <Card
          elevation={8}
          sx={{
            width: "100%",
            maxWidth: { xs: 320, sm: 600 },
            p: { xs: 2, sm: 4 },
            borderRadius: 3,
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.10)",
            // border: "1px solid #E2E8F0",
            background: "#FFFFFF",
            position: "relative",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              minWidth: 32,
              p: 0,
              color: "#4B5563",
              "&:hover": {
                color: "#1E3A8A",
                background: "transparent",
              },
            }}
          >
            <CloseIcon />
          </Button>

          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              height: isMobile ? 80 : 130,
              display: "block",
              mx: "auto",
              mb: 2,
              maxWidth: "100%",
            }}
          />

          <Typography
            variant={isMobile ? "h5" : "h4"}
            fontWeight={700}
            textAlign="center"
            sx={{
              fontFamily: "'Lora', serif",
              color: "#1E3A8A",
              mb: 3,
              fontSize: { xs: "1.2rem", sm: "2.2rem", md: "2.5rem" },
            }}
          >
            Product Order Confirmed
          </Typography>

          <Divider sx={{ my: 3, borderColor: "#E2E8F0", borderWidth: 1 }} />

          <Stack spacing={2}>
            <DetailRow label="Order Number" value={order_number} />
            <DetailRow
              label="Order Date"
              value={dayjs(order_date).format("YYYY-MM-DD")}
            />
            <DetailRow
              label="Delivery Date"
              value={dayjs(delivery_date).format("YYYY-MM-DD")}
            />
            <DetailRow
              label="Total Amount"
              value={`₹${parseFloat(total_amount).toFixed(2)}`}
            />
          </Stack>

          <Divider sx={{ my: 3, borderColor: "#E2E8F0", borderWidth: 1 }} />

          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
          >
            {renderActionButton(
              <PictureAsPdfIcon />,
              "View PDF",
              handleViewPDF,
              "#2563EB",
              "#1E40AF"
            )}
            {renderActionButton(
              <DownloadIcon />,
              "Download",
              handleDownloadPDF,
              "#059669",
              "#047857"
            )}
            {renderActionButton(
              <ShareIcon />,
              "Share",
              handleShare,
              "#6B7280",
              "#4B5563"
            )}
          </Stack>

          <Box mt={4} textAlign="center">
            <Button
              size={isMobile ? "medium" : "large"}
              onClick={handleTakeMoreOrder}
              sx={{
                borderRadius: 2,
                px: { xs: 4, sm: 6 },
                py: { xs: 1.2, sm: 1.5 },
                background: "#1E3A8A",
                color: "#FFFFFF",
                fontFamily: "'Roboto', sans-serif",
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.2rem" },
                fontWeight: 700,
                textTransform: "none",
                "&:hover": {
                  background: "#1E40AF",
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                  transform: "scale(1.02)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Order More Products
            </Button>
          </Box>
        </Card>
      </Fade>
    </Modal>
  );
};

const DetailRow = ({ label, value }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    sx={{
      background: "#F8FAFC",
      p: 1.5,
      borderRadius: 2,
      border: "1px solid #E2E8F0",
    }}
  >
    <Typography
      variant="body1"
      sx={{
        fontFamily: "'Roboto', sans-serif",
        color: "#4B5563",
        fontWeight: 500,
        fontSize: { xs: "0.9rem", sm: "1rem" },
      }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontFamily: "'Roboto', sans-serif",
        color: "#1E3A8A",
        fontWeight: 600,
        fontSize: { xs: "0.9rem", sm: "1rem" },
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default OrderConfirmModal;